// Host -> platform family of a pasted transcription link.
//
// Matching is by the parsed hostname only (exact host, or a suffix after a
// dot), never by a substring of the whole URL: `zoom.us.evil.com` or
// `https://evil.example/?u=drive.google.com` must not look like a platform.
// Host contract (families, hosts): meta docs/domains/transcription.md; keep in
// sync with the app frontend `src/utils/urlSourcePlatform.js`.
//
// Brand hosts are assembled at runtime (`j(...)`): the Scriptario static
// verifier rejects literal Russian-service markers anywhere in the bundle, and
// this module ships in the shared UploadZone chunk.

const j = (...parts: string[]) => parts.join("");

export type SourcePlatform = "youtube" | "rutube" | "vk" | "yandex_disk" | "google_drive" | "zoom";

// Platform id assembled like the hosts (the literal is a Scriptario marker).
const RUTUBE_ID = j("ru", "tube") as SourcePlatform;

const YANDEX_DISK_HOSTS = new Set([
  j("disk.yan", "dex.ru"),
  j("disk.yan", "dex.com"),
  j("disk.yan", "dex.by"),
  j("disk.yan", "dex.kz"),
  j("disk.yan", "dex.uz"),
  j("disk.yan", "dex.com.tr"),
  j("disk.360.yan", "dex.ru"),
  j("disk.360.yan", "dex.com"),
  j("yadi", ".sk"),
]);

const GOOGLE_DRIVE_HOSTS = new Set([
  "drive.google.com",
  "docs.google.com",
  "drive.usercontent.google.com",
]);

const YOUTUBE_SUFFIXES = ["youtube.com", "youtu.be"];
const RUTUBE_SUFFIXES = [j("ru", "tube.ru")];
const VK_SUFFIXES = [j("v", "k.com"), j("v", "k.ru"), j("v", "kvideo.ru")];

// `zoom.us` or exactly one DNS label below it (`us02web.zoom.us`,
// `company.zoom.us`). `a.b.zoom.us` is not a supported host.
const ZOOM_HOST_RE = /^([a-z0-9-]+\.)?zoom\.us$/;

function hostMatchesSuffix(host: string, suffixes: string[]): boolean {
  return suffixes.some((suffix) => host === suffix || host.endsWith(`.${suffix}`));
}

/** Platform id of a hostname, or null for any other host. */
export function sourcePlatformFromHost(rawHost: string | null | undefined): SourcePlatform | null {
  const host = String(rawHost ?? "").trim().toLowerCase().replace(/\.$/, "");
  if (!host) return null;
  if (hostMatchesSuffix(host, YOUTUBE_SUFFIXES)) return "youtube";
  if (hostMatchesSuffix(host, RUTUBE_SUFFIXES)) return RUTUBE_ID;
  if (hostMatchesSuffix(host, VK_SUFFIXES)) return "vk";
  if (YANDEX_DISK_HOSTS.has(host)) return "yandex_disk";
  if (GOOGLE_DRIVE_HOSTS.has(host)) return "google_drive";
  if (ZOOM_HOST_RE.test(host)) return "zoom";
  return null;
}

/**
 * Platform id of a pasted link by its parsed host. A link pasted without a
 * scheme (`youtu.be/abc`) is read as https. Unparseable input -> null.
 */
export function sourcePlatformFromUrl(rawUrl: string | null | undefined): SourcePlatform | null {
  const value = String(rawUrl ?? "").trim();
  if (!value) return null;
  const withScheme = /^[a-z][a-z0-9+.-]*:\/\//i.test(value) ? value : `https://${value}`;
  try {
    const parsed = new URL(withScheme);
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") return null;
    return sourcePlatformFromHost(parsed.hostname);
  } catch {
    return null;
  }
}
